"use client";

import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import HeaderMain from "../dasboard-component/Components/HeaderMain";
import { useAuth } from "../context/AuthContext";
import { useInstituteAuth } from "../context/InstituteAuthContext";
import { toast } from "react-toastify";

/**
 * JSON shape we persist per padel tennis match (always doubles)
 * {
 *   matchType: "best_of_3" | "best_of_5",
 *   gameMode: "doubles",
 *   teamA: { player1: string | null, player2: string | null },
 *   teamB: { player1: string | null, player2: string | null },
 *   sets: [{ userGames, opponentGames, userTiebreak, opponentTiebreak }],
 *   userSets: number,
 *   opponentSets: number,
 *   winner: "user" | "opponent" | null
 * }
 */

export const AddPadelTennis = () => {
  const location = useLocation();
  const data = location.state || {};

  const [padelMatches, setPadelMatches] = useState([]);
  const navigate = useNavigate();
  const [availableUsers, setAvailableUsers] = useState([]);
  const { user } = useAuth();
  const { instituteUser } = useInstituteAuth();

  // Check if this is institute context
  const isInstitute = data.isInstitute || false;
  const instituteId = data.instituteId || instituteUser?.id;

  // Fetch available users for player selection
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/users`,
          { credentials: "include" }
        );
        if (response.ok) {
          const users = await response.json();

          // Get current user ID
          const currentUserId = user?.uid;

          // Prioritize invited users and show their status
          const invitedUsers = (data.invitedUsers || []).map((user) => ({
            ...user,
            isInvited: true,
            invitationStatus: user.status,
          }));

          // Find current user and add them if not already in invited users
          const currentUser = users.find((user) => user.id == currentUserId);
          const currentUserIsInvited = invitedUsers.some(
            (invited) => invited.user_id == currentUserId
          );

          let finalUserList = [...invitedUsers];

          // Add current user if not already invited
          if (currentUser && !currentUserIsInvited) {
            finalUserList.push({
              ...currentUser,
              isCurrent: true,
            });
          }

          // Filter out invited users AND current user from the general list to avoid duplicates
          const otherUsers = users.filter(
            (user) =>
              !invitedUsers.some((invited) => invited.user_id === user.id) &&
              user.id != currentUserId
          );

          // Add other users
          finalUserList = [...finalUserList, ...otherUsers];

          setAvailableUsers(finalUserList);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [data.invitedUsers]);

  // Brand colors
  const brandBlue = "#3498db";
  const brandBlueDark = "#2980b9";

  // Tailwind class helpers for consistent styling
  const classes = {
    container:
      "max-w-6xl w-full mx-auto bg-white rounded-2xl shadow-lg font-sans mt-6 px-4 sm:px-6 lg:px-8 py-6 lg:py-8",
    header: "border-b-2 pb-4 mb-6",
    headerTitle: "text-3xl font-semibold text-gray-800 mb-2",
    headerSubtitle: "text-lg text-gray-600",
    addButton:
      "bg-[#3498db] hover:bg-[#2980b9] text-white font-semibold py-3 px-5 rounded-lg shadow-md transition-all duration-200 mb-5",
    saveButton:
      "w-full max-w-[260px] mx-auto block bg-emerald-500 text-white border-2 border-emerald-500 font-semibold py-3 px-4 rounded-lg shadow-md transition-all duration-200 mt-4 hover:bg-white hover:text-emerald-600 hover:-translate-y-0.5",
    noMatchesMessage:
      "text-center p-10 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 text-lg",
  };

  const validateMatches = () => {
    const errors = [];

    padelMatches.forEach((match, index) => {
      // Check if match is complete
      if (
        match.userSets < 2 &&
        match.opponentSets < 2 &&
        match.sets.length === 0
      ) {
        errors.push(
          `Match ${index + 1}: Match must have at least one set or be completed`
        );
      }

      // Validate incomplete sets
      match.sets.forEach((set, setIndex) => {
        if (set.userGames > 0 || set.opponentGames > 0) {
          // Check if set is properly completed
          const userWon =
            (set.userGames >= 6 && set.userGames >= set.opponentGames + 2) ||
            (set.userGames === 7 &&
              set.opponentGames === 6 &&
              set.userTiebreak > set.opponentTiebreak);
          const opponentWon =
            (set.opponentGames >= 6 &&
              set.opponentGames >= set.userGames + 2) ||
            (set.opponentGames === 7 &&
              set.userGames === 6 &&
              set.opponentTiebreak > set.userTiebreak);

          if (!userWon && !opponentWon) {
            errors.push(
              `Match ${index + 1}, Set ${
                setIndex + 1
              }: Set appears incomplete (needs to reach 6 games with 2-game margin or 7-6 with tiebreak)`
            );
          }

          // Check tiebreak validity
          if (
            (set.userGames === 7 && set.opponentGames === 6) ||
            (set.opponentGames === 7 && set.userGames === 6)
          ) {
            if (!set.userTiebreak || !set.opponentTiebreak) {
              errors.push(
                `Match ${index + 1}, Set ${
                  setIndex + 1
                }: Tiebreak scores are required for 7-6 sets`
              );
            }
          }
        }
      });
    });

    return errors;
  };

  const handleSaveAll = async () => {
    // Validate all matches before saving
    const validationErrors = validateAllMatches();
    if (validationErrors.length > 0) {
      toast.error(
        "Please fix the following issues before saving:\n\n" +
          validationErrors.join("\n")
      );
      return;
    }

    const validationErrors2 = validateMatches();
    if (validationErrors2.length > 0) {
      toast.error(
        "Please fix the following errors:\n\n" + validationErrors2.join("\n")
      );
      return;
    }

    try {
      const arenaBookingId =
        data.arenaBooking?.id === "0" ? null : data.arenaBooking?.id;

      const bodyData = {
        arenaBookingId,
        sport: data.sport,
        games: padelMatches,
      };

      // Add either userId or instituteId based on context
      if (isInstitute) {
        bodyData.instituteId = instituteId;
      } else {
        bodyData.userId = user?.uid;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/save-match`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(bodyData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save match");
      }

      const responseData = await response.json();
      toast.success("Saved all padel tennis matches!");

      // Navigate based on context
      if (isInstitute) {
        navigate("/institute-owner");
      } else {
        navigate("/Manage");
      }
    } catch (error) {
      console.error("Error saving match:", error);
      toast.error("Error saving match");
    }
  };

  // Enhanced validation function for all matches
  const validateAllMatches = () => {
    const errors = [];

    padelMatches.forEach((match, index) => {
      const matchErrors = validatePadelMatch(match, index + 1);
      errors.push(...matchErrors);
    });

    return errors;
  };

  // Validate individual padel match
  const validatePadelMatch = (match, matchNumber) => {
    const errors = [];

    // Check if all team players are selected (padel is always doubles)
    if (
      !match.teamA?.player1 ||
      !match.teamA?.player2 ||
      !match.teamB?.player1 ||
      !match.teamB?.player2
    ) {
      errors.push(
        `Match ${matchNumber}: All team players must be selected (Padel is always doubles)`
      );
    }

    // Check for duplicate players
    const allPlayers = [
      match.teamA?.player1,
      match.teamA?.player2,
      match.teamB?.player1,
      match.teamB?.player2,
    ].filter(Boolean);
    const uniquePlayers = [...new Set(allPlayers)];
    if (allPlayers.length !== uniquePlayers.length) {
      errors.push(
        `Match ${matchNumber}: Cannot select the same player multiple times`
      );
    }

    // Check if match has sets
    if (!match.sets || match.sets.length === 0) {
      errors.push(`Match ${matchNumber}: Match must have at least one set`);
    }

    // Validate set scores
    if (match.sets && match.sets.length > 0) {
      match.sets.forEach((set, setIndex) => {
        // Check for valid game scores (0-7, with proper padel scoring rules)
        if (set.teamAGames > 7 || set.teamBGames > 7) {
          errors.push(
            `Match ${matchNumber}, Set ${setIndex + 1}: Games cannot exceed 7`
          );
        }

        // Check for valid set completion
        if (set.teamAGames < 6 && set.teamBGames < 6) {
          errors.push(
            `Match ${matchNumber}, Set ${
              setIndex + 1
            }: At least one team must reach 6 games`
          );
        }

        // Check for proper set winning conditions
        if (set.teamAGames === 6 && set.teamBGames === 6) {
          // Tiebreak situation - check if tiebreak scores are provided
          if (!set.teamATiebreak || !set.teamBTiebreak) {
            errors.push(
              `Match ${matchNumber}, Set ${
                setIndex + 1
              }: Tiebreak scores required when games are 6-6`
            );
          } else if (set.teamATiebreak < 7 && set.teamBTiebreak < 7) {
            errors.push(
              `Match ${matchNumber}, Set ${
                setIndex + 1
              }: Tiebreak must reach at least 7 points`
            );
          }
        }

        // Check for proper margin in regular games
        if (set.teamAGames >= 6 || set.teamBGames >= 6) {
          const diff = Math.abs(set.teamAGames - set.teamBGames);
          if (diff < 2 && set.teamAGames < 7 && set.teamBGames < 7) {
            errors.push(
              `Match ${matchNumber}, Set ${
                setIndex + 1
              }: Must win by 2 games or reach 7 games`
            );
          }
        }
      });
    }

    // Check match completion (best of 3 sets)
    if (match.teamASets < 2 && match.teamBSets < 2) {
      errors.push(
        `Match ${matchNumber}: Match incomplete - need 2 set wins for best of 3`
      );
    }

    return errors;
  };

  const handleAddPadelMatch = () => {
    const newMatch = {
      matchType: "best_of_3", // Default to best of 3
      gameMode: "doubles", // Padel is always doubles
      sets: [],
      userSets: 0,
      opponentSets: 0,
      winner: null,
      // For doubles (typical for Padel Tennis)
      teamA: { player1: null, player2: null },
      teamB: { player1: null, player2: null },
    };
    setPadelMatches((prev) => [...prev, newMatch]);
  };

  const handleRemoveMatch = (index) => {
    setPadelMatches((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateMatch = (index, updatedMatch) => {
    setPadelMatches((prevMatches) =>
      prevMatches.map((m, i) => (i === index ? updatedMatch : m))
    );
  };

  return (
    <>
      <HeaderMain pageType="AddGame" />
      <div className={classes.container}>
        <div className={classes.header}>
          <h2 className={classes.headerTitle}>
            {data.sport} Match at {data.arenaBooking?.name}
          </h2>
          <p className={classes.headerSubtitle}>
            {data.arenaBooking?.date && data.arenaBooking?.time && (
              <>
                Booking: {data.arenaBooking.date} at {data.arenaBooking.time}
              </>
            )}
          </p>
        </div>

        <button className={classes.addButton} onClick={handleAddPadelMatch}>
          Add New Padel Tennis Match
        </button>

        {padelMatches.length === 0 && (
          <div className={classes.noMatchesMessage}>
            No matches added yet. Click the button above to add your first padel
            tennis match.
          </div>
        )}

        {padelMatches.map((match, index) => (
          <PadelMatchForm
            key={index}
            match={match}
            matchNumber={index + 1}
            availableUsers={availableUsers}
            onUpdate={(updatedMatch) => handleUpdateMatch(index, updatedMatch)}
            onRemove={() => handleRemoveMatch(index)}
          />
        ))}

        {padelMatches.length > 0 && (
          <button onClick={handleSaveAll} className={classes.saveButton}>
            Save All Matches
          </button>
        )}
      </div>
    </>
  );
};

const PadelMatchForm = ({
  match,
  matchNumber,
  availableUsers,
  onUpdate,
  onRemove,
}) => {
  const [localMatch, setLocalMatch] = useState(match);

  const formClasses = {
    matchCard:
      "border border-gray-200 rounded-xl p-5 mb-6 bg-gray-50 shadow-sm",
    matchHeader:
      "flex justify-between items-start md:items-center flex-col md:flex-row gap-3 border-b border-gray-200 pb-4 mb-5",
    matchTitle: "text-xl font-semibold text-gray-800",
    removeButton:
      "bg-red-400 hover:bg-red-500 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200",
    formGroup: "space-y-4 mb-5",
    label: "font-medium text-gray-700 block mb-2",
    select:
      "w-full max-w-[240px] px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
    addButton:
      "bg-[#3498db] hover:bg-[#2980b9] text-white font-medium px-3 py-2 rounded-lg transition-colors duration-200",
    tableWrapper: "w-full overflow-x-auto",
    table:
      "w-full text-sm border-collapse mt-4 rounded-lg overflow-hidden shadow-sm",
    tableHeader:
      "bg-gray-100 text-gray-800 font-semibold p-3 text-left border-b border-gray-200",
    tableCell: "p-3 border-b border-gray-200 bg-white",
    numberInput: "w-20 px-3 py-2 border border-gray-300 rounded text-center",
    scoreCard:
      "flex justify-between bg-white p-5 rounded-lg mt-5 shadow-sm flex-col md:flex-row gap-3",
    scoreBox:
      "text-center p-4 rounded-lg min-w-[120px] bg-gray-50 w-full md:w-auto",
    scoreLabel: "text-sm text-gray-500 mb-1",
    scoreValue: "text-2xl font-bold text-blue-800",
    winnerBox:
      "text-center p-4 rounded-lg bg-blue-50 min-w-[120px] w-full md:w-auto",
    guidanceBox:
      "bg-blue-50 border border-blue-200 rounded-lg p-5 shadow-sm mb-5",
    guidanceTitle:
      "text-lg font-semibold text-blue-800 mb-3 flex items-center gap-2",
    guidanceContent: "text-blue-700 space-y-2 text-sm leading-relaxed",
    playerSection: "bg-white p-4 rounded-lg shadow-sm mb-4",
    playerRow: "grid grid-cols-1 md:grid-cols-2 gap-4",
  };

  const updateParent = (updated) => {
    setLocalMatch(updated);
    onUpdate(updated);
  };

  const handleMatchTypeChange = (e) => {
    const updated = { ...localMatch, matchType: e.target.value };
    updateParent(updated);
  };

  const handleAddSet = () => {
    const newSet = {
      userGames: 0,
      opponentGames: 0,
      userTiebreak: null,
      opponentTiebreak: null,
    };
    const updated = {
      ...localMatch,
      sets: [...localMatch.sets, newSet],
    };
    recalculateMatchResults(updated);
  };

  const handleSetChange = (index, field, value) => {
    // Validate and limit input values
    let limitedValue = Number(value);

    if (field === "userGames" || field === "opponentGames") {
      // Limit games to 0-7
      limitedValue = Math.max(0, Math.min(7, limitedValue));
    } else if (field === "userTiebreak" || field === "opponentTiebreak") {
      // Limit tiebreak to 0-20 (reasonable upper limit)
      limitedValue = Math.max(0, Math.min(20, limitedValue));
    }

    const updatedSets = localMatch.sets.map((set, i) => {
      if (i === index) {
        return { ...set, [field]: limitedValue };
      }
      return set;
    });
    const updated = { ...localMatch, sets: updatedSets };
    recalculateMatchResults(updated);
  };

  const handleRemoveSet = (index) => {
    const updatedSets = localMatch.sets.filter((_, i) => i !== index);
    const updated = { ...localMatch, sets: updatedSets };
    recalculateMatchResults(updated);
  };

  const recalculateMatchResults = (updatedMatch) => {
    let userSets = 0;
    let opponentSets = 0;

    updatedMatch.sets.forEach((set) => {
      // Padel Tennis set scoring: first to 6 games (must win by 2), or 7-6 with tiebreak
      if (set.userGames >= 6 && set.userGames >= set.opponentGames + 2) {
        userSets++;
      } else if (
        set.userGames === 7 &&
        set.opponentGames === 6 &&
        set.userTiebreak > set.opponentTiebreak
      ) {
        userSets++;
      } else if (
        set.opponentGames >= 6 &&
        set.opponentGames >= set.userGames + 2
      ) {
        opponentSets++;
      } else if (
        set.opponentGames === 7 &&
        set.userGames === 6 &&
        set.opponentTiebreak > set.userTiebreak
      ) {
        opponentSets++;
      }
    });

    updatedMatch.userSets = userSets;
    updatedMatch.opponentSets = opponentSets;

    // Determine match winner (best of 3 sets in padel)
    if (userSets >= 2) {
      updatedMatch.winner = "user";
    } else if (opponentSets >= 2) {
      updatedMatch.winner = "opponent";
    } else {
      updatedMatch.winner = null;
    }

    updateParent(updatedMatch);
  };

  return (
    <div className={formClasses.matchCard}>
      <div className={formClasses.matchHeader}>
        <h3 className={formClasses.matchTitle}>
          Padel Tennis Match #{matchNumber}
        </h3>
        <button onClick={onRemove} className={formClasses.removeButton}>
          Remove Match
        </button>
      </div>

      {/* Padel Tennis Scoring Guidance */}
      <div className={formClasses.guidanceBox}>
        <h4 className={formClasses.guidanceTitle}>
          <span>🎾</span>
          <span>Padel Tennis Scoring Guide</span>
        </h4>
        <div className={formClasses.guidanceContent}>
          <p>
            <strong>Game Format:</strong> Always doubles (2 vs 2) - all 4
            players must be selected
          </p>
          <p>
            <strong>Match Format:</strong> Best of 3 sets (first team to win 2
            sets)
          </p>
          <p>
            <strong>Set Scoring:</strong> First to 6 games wins the set (must
            win by 2 games)
          </p>
          <p>
            <strong>Tiebreak:</strong> When games are 6-6, play tiebreak to 7
            points (must win by 2)
          </p>
          <p>
            <strong>Games Range:</strong> 0-7 games per set (7-6 with tiebreak
            or 7-5 without)
          </p>
        </div>
      </div>

      <div className={formClasses.formGroup}>
        <label className={formClasses.label}>Match Type:</label>
        <select
          value={localMatch.matchType}
          onChange={handleMatchTypeChange}
          className={formClasses.select}
        >
          <option value="best_of_3">Best of 3</option>
          <option value="best_of_5">Best of 5</option>
        </select>
      </div>

      {/* Team Selection for Doubles */}
      <div className={formClasses.playerSection}>
        <h4 className="text-lg font-semibold text-gray-700 mb-3">Team A</h4>
        <div className={formClasses.playerRow}>
          <div>
            <label className={formClasses.label}>Player 1:</label>
            <PlayerSlot
              availableUsers={availableUsers}
              selectedPlayerId={localMatch.teamA?.player1}
              selectedUserIds={[
                localMatch.teamA?.player1,
                localMatch.teamA?.player2,
                localMatch.teamB?.player1,
                localMatch.teamB?.player2,
              ].filter((id) => id && !String(id).startsWith("custom_"))}
              onSelectPlayer={(userInfo, customName) => {
                const playerId =
                  userInfo?.id ||
                  userInfo?.user_id ||
                  (customName ? `custom_${Date.now()}` : null);
                const updated = {
                  ...localMatch,
                  teamA: {
                    ...localMatch.teamA,
                    player1: playerId ? String(playerId) : null,
                  },
                };
                updateParent(updated);
              }}
            />
          </div>
          <div>
            <label className={formClasses.label}>Player 2:</label>
            <PlayerSlot
              availableUsers={availableUsers}
              selectedPlayerId={localMatch.teamA?.player2}
              selectedUserIds={[
                localMatch.teamA?.player1,
                localMatch.teamA?.player2,
                localMatch.teamB?.player1,
                localMatch.teamB?.player2,
              ].filter((id) => id && !String(id).startsWith("custom_"))}
              onSelectPlayer={(userInfo, customName) => {
                const playerId =
                  userInfo?.id ||
                  userInfo?.user_id ||
                  (customName ? `custom_${Date.now()}` : null);
                const updated = {
                  ...localMatch,
                  teamA: {
                    ...localMatch.teamA,
                    player2: playerId ? String(playerId) : null,
                  },
                };
                updateParent(updated);
              }}
            />
          </div>
        </div>
      </div>

      <div className={formClasses.playerSection}>
        <h4 className="text-lg font-semibold text-gray-700 mb-3">Team B</h4>
        <div className={formClasses.playerRow}>
          <div>
            <label className={formClasses.label}>Player 1:</label>
            <PlayerSlot
              availableUsers={availableUsers}
              selectedPlayerId={localMatch.teamB?.player1}
              selectedUserIds={[
                localMatch.teamA?.player1,
                localMatch.teamA?.player2,
                localMatch.teamB?.player1,
                localMatch.teamB?.player2,
              ].filter((id) => id && !String(id).startsWith("custom_"))}
              onSelectPlayer={(userInfo, customName) => {
                const playerId =
                  userInfo?.id ||
                  userInfo?.user_id ||
                  (customName ? `custom_${Date.now()}` : null);
                const updated = {
                  ...localMatch,
                  teamB: {
                    ...localMatch.teamB,
                    player1: playerId ? String(playerId) : null,
                  },
                };
                updateParent(updated);
              }}
            />
          </div>
          <div>
            <label className={formClasses.label}>Player 2:</label>
            <PlayerSlot
              availableUsers={availableUsers}
              selectedPlayerId={localMatch.teamB?.player2}
              selectedUserIds={[
                localMatch.teamA?.player1,
                localMatch.teamA?.player2,
                localMatch.teamB?.player1,
                localMatch.teamB?.player2,
              ].filter((id) => id && !String(id).startsWith("custom_"))}
              onSelectPlayer={(userInfo, customName) => {
                const playerId =
                  userInfo?.id ||
                  userInfo?.user_id ||
                  (customName ? `custom_${Date.now()}` : null);
                const updated = {
                  ...localMatch,
                  teamB: {
                    ...localMatch.teamB,
                    player2: playerId ? String(playerId) : null,
                  },
                };
                updateParent(updated);
              }}
            />
          </div>
        </div>
      </div>

      <div>
        <button onClick={handleAddSet} className={formClasses.addButton}>
          Add Set
        </button>
      </div>

      {localMatch.sets.length > 0 && (
        <div className={formClasses.tableWrapper}>
          <table className={formClasses.table}>
            <thead>
              <tr>
                <th className={formClasses.tableHeader}>Set</th>
                <th className={formClasses.tableHeader}>User Games</th>
                <th className={formClasses.tableHeader}>Opponent Games</th>
                <th className={formClasses.tableHeader}>User Tiebreak</th>
                <th className={formClasses.tableHeader}>Opponent Tiebreak</th>
                <th className={formClasses.tableHeader}>Action</th>
              </tr>
            </thead>
            <tbody>
              {localMatch.sets.map((set, i) => (
                <tr key={i}>
                  <td
                    className={`${formClasses.tableCell} font-semibold bg-gray-50`}
                  >
                    {i + 1}
                  </td>
                  <td className={formClasses.tableCell}>
                    <input
                      type="number"
                      value={set.userGames}
                      onChange={(e) =>
                        handleSetChange(i, "userGames", e.target.value)
                      }
                      min="0"
                      max="7"
                      className={formClasses.numberInput}
                    />
                  </td>
                  <td className={formClasses.tableCell}>
                    <input
                      type="number"
                      value={set.opponentGames}
                      onChange={(e) =>
                        handleSetChange(i, "opponentGames", e.target.value)
                      }
                      min="0"
                      max="7"
                      className={formClasses.numberInput}
                    />
                  </td>
                  <td className={formClasses.tableCell}>
                    <input
                      type="number"
                      value={set.userTiebreak || ""}
                      onChange={(e) =>
                        handleSetChange(i, "userTiebreak", e.target.value)
                      }
                      min="0"
                      className={formClasses.numberInput}
                      placeholder="N/A"
                    />
                  </td>
                  <td className={formClasses.tableCell}>
                    <input
                      type="number"
                      value={set.opponentTiebreak || ""}
                      onChange={(e) =>
                        handleSetChange(i, "opponentTiebreak", e.target.value)
                      }
                      min="0"
                      className={formClasses.numberInput}
                      placeholder="N/A"
                    />
                  </td>
                  <td className={formClasses.tableCell}>
                    <button
                      onClick={() => handleRemoveSet(i)}
                      className="bg-red-400 hover:bg-red-500 text-white px-3 py-1.5 rounded text-xs cursor-pointer transition-colors duration-200"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Display scores and winner */}
      <div className={formClasses.scoreCard}>
        <div className={`${formClasses.scoreBox} bg-gray-50`}>
          <div className={formClasses.scoreLabel}>User Sets Won</div>
          <div className={formClasses.scoreValue}>{localMatch.userSets}</div>
        </div>

        <div className={formClasses.winnerBox}>
          <div className={formClasses.scoreLabel}>Match Status</div>
          <div className={formClasses.scoreValue}>
            {localMatch.winner === null
              ? "In Progress"
              : localMatch.winner === "user"
              ? "User Wins"
              : "Opponent Wins"}
          </div>
        </div>

        <div className={`${formClasses.scoreBox} bg-gray-50`}>
          <div className={formClasses.scoreLabel}>Opponent Sets Won</div>
          <div className={formClasses.scoreValue}>
            {localMatch.opponentSets}
          </div>
        </div>
      </div>

      {/* Info about padel scoring */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4 text-sm text-blue-700">
        <strong>Padel Scoring:</strong> Best of 3 sets. First to 6 games (must
        win by 2), or 7-6 with tiebreak.
      </div>
    </div>
  );
};

// PlayerSlot component for single player selection per slot
const PlayerSlot = ({
  availableUsers,
  selectedPlayerId,
  onSelectPlayer,
  selectedUserIds = [],
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [customName, setCustomName] = useState("");
  const [useCustomName, setUseCustomName] = useState(false);
  const [customNamesMap, setCustomNamesMap] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false); // Store custom names by playerId

  // Helper function to get player name from ID
  const getPlayerName = (playerId) => {
    if (!playerId) return null;
    const playerIdStr = String(playerId);
    if (playerIdStr.startsWith("custom_"))
      return customNamesMap[playerIdStr] || "Custom Player";
    const user = availableUsers.find(
      (u) => String(u.id || u.user_id) === playerIdStr
    );
    return user?.full_name || "Unknown Player";
  };

  const playerName = getPlayerName(selectedPlayerId);
  const isSlotEmpty = !selectedPlayerId || !playerName;

  // Fetch user suggestions from API
  const fetchUserSuggestions = async (term) => {
    if (!term || term.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/common/search-users?term=${term}`
      );
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
      }
    } catch (error) {
      console.error("Error fetching user suggestions:", error);
      setSuggestions([]);
    }
  };

  // Debounced search
  useEffect(() => {
    if (!useCustomName && searchTerm) {
      const timer = setTimeout(() => {
        fetchUserSuggestions(searchTerm);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSuggestions([]);
    }
  }, [searchTerm, useCustomName]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowSuggestions(true);
    setSelectedUser(null);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchTerm(user.full_name || user.username);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleSave = () => {
    if (useCustomName && customName.trim()) {
      const customPlayerId = `custom_${Date.now()}`;
      setCustomNamesMap((prev) => ({
        ...prev,
        [customPlayerId]: customName.trim(),
      }));
      onSelectPlayer(
        { id: customPlayerId, full_name: customName.trim() },
        customName.trim()
      );
      setCustomName("");
      setIsEditing(false);
    } else if (!useCustomName && selectedUser) {
      onSelectPlayer(selectedUser);
      setSelectedUser(null);
      setSearchTerm("");
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setSelectedUser(null);
    setSearchTerm("");
    setCustomName("");
    setUseCustomName(false);
    setIsEditing(false);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Show editing UI if editing or no player selected
  if (isEditing || isSlotEmpty) {
    return (
      <div className="flex flex-col gap-2">
        {!useCustomName ? (
          <>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => {
                  setTimeout(() => setShowSuggestions(false), 200);
                }}
                placeholder="Search for system users..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 border-t-0 rounded-b-lg max-h-48 overflow-y-auto z-50 shadow-lg">
                  {suggestions.map((user) => {
                    const userId = String(user.id || user.user_id);
                    const currentPlayerId = String(selectedPlayerId || "");
                    const isAlreadySelected =
                      selectedUserIds
                        .map((id) => String(id))
                        .includes(userId) && userId !== currentPlayerId;
                    return (
                      <div
                        key={user.id || user.user_id}
                        className={`p-3 cursor-pointer border-b border-gray-100 transition-colors ${
                          isAlreadySelected
                            ? "opacity-50 bg-gray-100 cursor-not-allowed"
                            : "hover:bg-gray-50"
                        }`}
                        onClick={() =>
                          !isAlreadySelected && handleSelectUser(user)
                        }
                      >
                        <div className="font-medium text-gray-900">
                          {user.full_name || user.username}
                          {isAlreadySelected && (
                            <span className="text-red-500 ml-2">
                              (Already Added)
                            </span>
                          )}
                        </div>
                        {user.email && (
                          <div className="text-xs text-gray-500">
                            {user.email}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {showSuggestions &&
                searchTerm.length >= 2 &&
                suggestions.length === 0 && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 border-t-0 rounded-b-lg shadow-lg">
                    <div className="p-3 text-sm text-gray-500 text-center">
                      No users found
                    </div>
                  </div>
                )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={!selectedUser}
                className={`bg-blue-500 text-white px-4 py-2 rounded-lg text-sm transition-all ${
                  selectedUser
                    ? "hover:bg-blue-600"
                    : "opacity-50 cursor-not-allowed"
                }`}
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-lg text-sm transition-colors"
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <input
              type="text"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder="Enter custom player name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={!customName.trim()}
                className={`bg-blue-500 text-white px-4 py-2 rounded-lg text-sm transition-all ${
                  customName.trim()
                    ? "hover:bg-blue-600"
                    : "opacity-50 cursor-not-allowed"
                }`}
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-lg text-sm transition-colors"
              >
                Cancel
              </button>
            </div>
          </>
        )}
        <span
          onClick={() => setUseCustomName(!useCustomName)}
          className="text-xs text-blue-500 cursor-pointer hover:underline"
        >
          {useCustomName ? "Use system users" : "Add custom name"}
        </span>
      </div>
    );
  }

  // If player is selected and not editing, show the selected player
  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
      <span className="text-sm text-gray-800 flex-1">{playerName}</span>
      <button
        onClick={() => {
          // If editing a custom player, populate the custom name
          if (
            selectedPlayerId &&
            String(selectedPlayerId).startsWith("custom_")
          ) {
            const existingCustomName = customNamesMap[String(selectedPlayerId)];
            if (existingCustomName) {
              setCustomName(existingCustomName);
              setUseCustomName(true);
            }
          }
          setIsEditing(true);
        }}
        className="bg-orange-400 hover:bg-orange-500 text-white px-3 py-1 rounded text-xs transition-colors"
      >
        Edit
      </button>
    </div>
  );
};

export default AddPadelTennis;
